import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { Button, Form, Row } from "react-bootstrap";
import moment from 'moment';
import axios from "axios";
import { emptyProgram, Program, ProgramMedia, ProgramFolder } from "../../../../interfaces/program";
import { getProgramMedia, getProgramSingle, getProgramFolders, moveProgramFolder, updateProgramMedia, createProgramMedia } from "../../../../services/program";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ProgramMediaNew } from "./program-media-new";
import { ProgramMediaEdit } from "./program-media-edit";
import { ProgramMediaDelete } from "./program-media-delete";
import { ProgramFolderNew } from "./program-folder-new";
import { ProgramFolderEdit } from "./program-folder-edit";
import { ProgramFolderDelete } from "./program-folder-delete";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { useDropzone } from "react-dropzone";
import { createMedia } from "../../../../services/media";
import { Media } from "../../../../interfaces/media";
import { UserContext } from "../../../../App";

export function ProgramMediaFolderView() {
    const params: any = useParams();
    const navigate = useNavigate();
    const { userProfile } = useContext(UserContext);

    const is_athlete = (userProfile.role.filter((r: any) => r.id == 'badminton_athlete').length > 0);

    const [program, setProgram] = useState<Program>(emptyProgram);
    const [programMedia, setProgramMedia] = useState<ProgramMedia[]>([]);
    const [allProgramMedia, setAllProgramMedia] = useState<ProgramMedia[]>([]);
    const [allFolders, setAllFolders] = useState<ProgramFolder[]>([]);
    const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
    const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());

    const [showAddNewMedia, setShowAddNewMedia] = useState(false);
    const [showEditMedia, setShowEditMedia] = useState(false);
    const [showDeleteMedia, setShowDeleteMedia] = useState(false);
    const [selectedMediaId, setSelectedMediaId] = useState(0);

    const [showAddNewFolder, setShowAddNewFolder] = useState(false);
    const [showEditFolder, setShowEditFolder] = useState(false);
    const [showDeleteFolder, setShowDeleteFolder] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState(0);
    const [parentFolderIdForNew, setParentFolderIdForNew] = useState<number | null>(null);

    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; folderId: number | null; mediaId: number | null } | null>(null);
    const [draggedFolder, setDraggedFolder] = useState<number | null>(null);
    const [dragOverFolder, setDragOverFolder] = useState<number | null>(null);
    const [dropPosition, setDropPosition] = useState<'before' | 'inside' | 'after' | null>(null);
    const [draggedMedia, setDraggedMedia] = useState<number | null>(null);

    const [loaded, setLoaded] = useState(false);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{name: string, progress: number, status: 'uploading' | 'completed' | 'error'}[]>([]);
    const [selectedItems, setSelectedItems] = useState<{type: 'folder' | 'media', id: number}[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortMode, setSortMode] = useState<'alphabetical' | 'date'>('alphabetical');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (loaded) {
            loadMediaInFolder(currentFolderId);
        }
    }, [currentFolderId]);

    useEffect(() => {
        const handleClick = () => setContextMenu(null);
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    const loadData = () => {
        Promise.all([
            getProgramSingle(params.id),
            getProgramFolders(params.id),
            getProgramMedia(params.id)
        ]).then(response => {
            setProgram(response[0].data);
            setAllFolders(response[1].data);
            setAllProgramMedia(response[2].data);
            setProgramMedia(response[2].data);
            setLoaded(true);
        });
    }

    // Solo el propietario puede gestionar contenido (cargar, editar, eliminar archivos y carpetas)
    const can_manage_content = !is_athlete && (program.created_by == userProfile.id);

    const loadMediaInFolder = (folderId: number | null) => {
        setLoadingMedia(true);
        getProgramMedia(params.id, '', folderId)
            .then(response => {
                setProgramMedia(response.data);
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                setLoadingMedia(false);
            });
    }

    const getRootFolders = (): ProgramFolder[] => {
        return allFolders
            .filter(f => f.parent_folder_id === null)
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    const getSubfolders = (parentId: number | null): ProgramFolder[] => {
        return allFolders
            .filter(f => f.parent_folder_id === parentId)
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    const hasSubfolders = (folderId: number): boolean => {
        return allFolders.some(f => f.parent_folder_id === folderId);
    }

    const toggleFolder = (folderId: number) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(folderId)) {
            newExpanded.delete(folderId);
        } else {
            newExpanded.add(folderId);
        }
        setExpandedFolders(newExpanded);
    }

    const getAllFolderIds = (folderId: number | null): number[] => {
        if (folderId === null) {
            return [];
        }
        const ids = [folderId];
        const children = allFolders.filter(f => f.parent_folder_id === folderId);
        children.forEach(child => {
            ids.push(...getAllFolderIds(child.id));
        });
        return ids;
    }

    const getAllDescendantFolders = (folderId: number): ProgramFolder[] => {
        const children = allFolders.filter(f => f.parent_folder_id === folderId);
        let descendants = [...children];
        children.forEach(child => {
            descendants = [...descendants, ...getAllDescendantFolders(child.id)];
        });
        return descendants;
    }

    const getMediaInFolder = (folderId: number): ProgramMedia[] => {
        return programMedia.filter(m => m.folder_id === folderId);
    }

    const getBreadcrumbPath = (): ProgramFolder[] => {
        if (currentFolderId === null) {
            return [];
        }

        const path: ProgramFolder[] = [];
        let folderId: number | null = currentFolderId;

        while (folderId !== null) {
            const folder = allFolders.find(f => f.id === folderId);
            if (folder) {
                path.unshift(folder);
                folderId = folder.parent_folder_id;
            } else {
                break;
            }
        }

        return path;
    }

    const getFilteredSubfolders = (): ProgramFolder[] => {
        let subfolders = getSubfolders(currentFolderId);
        if (searchTerm && searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            subfolders = subfolders.filter(folder => folder.name.toLowerCase().includes(term));
        }

        // En modo alfabético, ordenar según la dirección
        if (sortMode === 'alphabetical') {
            subfolders.sort((a, b) => {
                const comparison = a.name.localeCompare(b.name);
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return subfolders;
    }

    const getFilteredMedia = (): ProgramMedia[] => {
        let media = programMedia;
        if (searchTerm && searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            media = media.filter(m => m.name.toLowerCase().includes(term));
        }

        // Ordenar alfabéticamente
        if (sortMode === 'alphabetical') {
            media.sort((a, b) => {
                const comparison = a.name.localeCompare(b.name);
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return media;
    }

    const getAllItemsSortedByDate = (): Array<{type: 'folder' | 'media', item: ProgramFolder | ProgramMedia}> => {
        const folders = getSubfolders(currentFolderId);
        const media = programMedia;

        // Aplicar filtro de búsqueda
        let filteredFolders = folders;
        let filteredMedia = media;
        if (searchTerm && searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            filteredFolders = folders.filter(folder => folder.name.toLowerCase().includes(term));
            filteredMedia = media.filter(m => m.name.toLowerCase().includes(term));
        }

        const folderItems = filteredFolders.map(f => ({ type: 'folder' as const, item: f }));
        const mediaItems = filteredMedia.map(m => ({ type: 'media' as const, item: m }));
        const allItems = [...folderItems, ...mediaItems];

        // Ordenar por fecha de modificación
        allItems.sort((a, b) => {
            const dateA = new Date(a.item.changed_at).getTime();
            const dateB = new Date(b.item.changed_at).getTime();
            const comparison = dateB - dateA; // Más reciente primero por defecto
            return sortDirection === 'asc' ? -comparison : comparison;
        });

        return allItems;
    }

    const toggleSortDirection = () => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    }

    const handleFolderClick = (event: React.MouseEvent, folderId: number | null) => {
        event.stopPropagation();

        // Si presiona Ctrl/Cmd, es selección múltiple (solo para carpetas en la lista, no para navegación)
        if ((event.ctrlKey || event.metaKey) && folderId !== null && event.currentTarget.classList.contains('folder-item-list')) {
            event.preventDefault();
            toggleItemSelection('folder', folderId);
            return;
        }

        // Click normal = navegar y limpiar selección y búsqueda
        setCurrentFolderId(folderId);
        setSelectedItems([]);
        setSearchTerm('');
    }

    const toggleItemSelection = (type: 'folder' | 'media', id: number) => {
        setSelectedItems(prev => {
            const exists = prev.find(item => item.type === type && item.id === id);
            if (exists) {
                return prev.filter(item => !(item.type === type && item.id === id));
            } else {
                return [...prev, { type, id }];
            }
        });
    }

    const isItemSelected = (type: 'folder' | 'media', id: number): boolean => {
        return selectedItems.some(item => item.type === type && item.id === id);
    }

    const handleFolderToggle = (event: React.MouseEvent, folderId: number) => {
        event.stopPropagation();
        toggleFolder(folderId);
    }

    const handleFolderContextMenu = (event: React.MouseEvent, folderId: number | null) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            folderId: folderId !== null ? folderId : -1,
            mediaId: null
        });
    }

    const handleAreaContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            folderId: -1,
            mediaId: null
        });
    }

    const handleMediaContextMenu = (event: React.MouseEvent, mediaId: number) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            folderId: null,
            mediaId: mediaId
        });
    }

    // Media handlers
    const handleAddNewMedia = (event: any, folderId: number | null = null) => {
        event.preventDefault();
        if (folderId !== null) {
            setCurrentFolderId(folderId);
        }
        setShowAddNewMedia(true);
        setContextMenu(null);
    }

    const handleAddNewMediaOnSave = (pm: ProgramMedia) => {
        const newProgramMedia = [pm, ...programMedia];
        setProgramMedia(newProgramMedia);
        setShowAddNewMedia(false);
    }

    const handleAddNewMediaOnClose = () => {
        setShowAddNewMedia(false);
    }

    const handleDeleteMedia = (event: any) => {
        event.preventDefault();
        setSelectedMediaId(event.currentTarget.dataset.id);
        setShowDeleteMedia(true);
    }

    const handleDeleteMediaOnDelete = (id: number) => {
        setSelectedMediaId(0);
        const newProgramMedia = programMedia.filter((m: ProgramMedia) => m.id != id);
        setProgramMedia(newProgramMedia);
        setShowDeleteMedia(false);
    }

    const handleDeleteMediaOnClose = () => {
        setSelectedMediaId(0);
        setShowDeleteMedia(false);
    }

    const handleEditMedia = (event: any) => {
        event.preventDefault();
        setSelectedMediaId(event.currentTarget.dataset.id);
        setShowEditMedia(true);
    }

    const handleEditMediaOnSave = (changedProgramMedia: ProgramMedia) => {
        setSelectedMediaId(0);
        const newProgramMedia = programMedia.map((pm: ProgramMedia) => {
            if (pm.id == changedProgramMedia.id) {
                return changedProgramMedia;
            } else {
                return pm;
            }
        });
        setProgramMedia(newProgramMedia);
        setShowEditMedia(false);
    }

    const handleEditMediaOnClose = () => {
        setSelectedMediaId(0);
        setShowEditMedia(false);
    }

    // Folder handlers
    const handleAddNewFolder = (event: any, parentId: number | null = null) => {
        event.preventDefault();
        setParentFolderIdForNew(parentId);
        setShowAddNewFolder(true);
        setContextMenu(null);
    }

    const handleAddNewFolderOnSave = (folder: ProgramFolder) => {
        const newFolders = [...allFolders, folder];
        setAllFolders(newFolders);
        setShowAddNewFolder(false);
        setParentFolderIdForNew(null);
    }

    const handleAddNewFolderOnClose = () => {
        setShowAddNewFolder(false);
        setParentFolderIdForNew(null);
    }

    const handleDeleteFolder = (event: any, folderId: number) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedFolderId(folderId);
        setShowDeleteFolder(true);
    }

    const handleDeleteFolderOnDelete = (id: number) => {
        setSelectedFolderId(0);
        const newFolders = allFolders.filter((f: ProgramFolder) => f.id != id);
        setAllFolders(newFolders);
        setShowDeleteFolder(false);
    }

    const handleDeleteFolderOnClose = () => {
        setSelectedFolderId(0);
        setShowDeleteFolder(false);
    }

    const handleEditFolder = (event: any, folderId: number) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedFolderId(folderId);
        setShowEditFolder(true);
    }

    const handleEditFolderOnSave = (changedFolder: ProgramFolder) => {
        setSelectedFolderId(0);
        const newFolders = allFolders.map((f: ProgramFolder) => {
            if (f.id == changedFolder.id) {
                return changedFolder;
            } else {
                return f;
            }
        });
        setAllFolders(newFolders);
        setShowEditFolder(false);
    }

    const handleEditFolderOnClose = () => {
        setSelectedFolderId(0);
        setShowEditFolder(false);
    }

    // Drag & Drop handlers
    const handleDragStart = (event: React.DragEvent, folderId: number) => {
        event.stopPropagation();

        // Los coaches no pueden mover carpetas
        if (!can_manage_content) {
            event.preventDefault();
            return;
        }

        // Si la carpeta arrastrada está en la selección, arrastrar todas las seleccionadas
        const isSelected = isItemSelected('folder', folderId);
        if (isSelected && selectedItems.length > 0) {
            // Arrastrar múltiples elementos
            setDraggedFolder(folderId); // La principal
        } else {
            // Arrastrar solo esta carpeta y limpiar selección
            setDraggedFolder(folderId);
            setSelectedItems([]);
        }

        event.dataTransfer.effectAllowed = 'move';
    }

    const handleDragOver = (event: React.DragEvent, folderId: number | null) => {
        event.preventDefault();
        event.stopPropagation();

        // Si estamos arrastrando un archivo, siempre es "inside"
        if (draggedMedia) {
            event.dataTransfer.dropEffect = 'move';
            setDragOverFolder(folderId);
            setDropPosition('inside');
            return;
        }

        // Si estamos arrastrando una carpeta
        if (!draggedFolder || draggedFolder === folderId) {
            event.dataTransfer.dropEffect = 'none';
            setDragOverFolder(null);
            setDropPosition(null);
            return;
        }

        // No permitir soltar en un descendiente
        if (folderId !== null && isDescendant(folderId, draggedFolder)) {
            event.dataTransfer.dropEffect = 'none';
            setDragOverFolder(null);
            setDropPosition(null);
            return;
        }

        // Las carpetas solo se pueden mover "inside" (dentro de otra carpeta o root)
        // No se permite reordenar carpetas (before/after)
        event.dataTransfer.dropEffect = 'move';
        setDragOverFolder(folderId);
        setDropPosition('inside');
    }

    const handleDragOverInList = (event: React.DragEvent, folderId: number) => {
        event.preventDefault();
        event.stopPropagation();

        // Si estamos arrastrando un archivo, permitir drop "inside" para moverlo a la carpeta
        if (draggedMedia) {
            event.dataTransfer.dropEffect = 'move';
            setDragOverFolder(folderId);
            setDropPosition('inside');
            return;
        }

        // Si estamos arrastrando una carpeta
        if (!draggedFolder || draggedFolder === folderId) {
            event.dataTransfer.dropEffect = 'none';
            setDragOverFolder(null);
            setDropPosition(null);
            return;
        }

        // No permitir soltar en un descendiente
        if (isDescendant(folderId, draggedFolder)) {
            event.dataTransfer.dropEffect = 'none';
            setDragOverFolder(null);
            setDropPosition(null);
            return;
        }

        // Las carpetas solo se pueden mover "inside" (dentro de otra carpeta)
        // No se permite reordenar carpetas
        event.dataTransfer.dropEffect = 'move';
        setDragOverFolder(folderId);
        setDropPosition('inside');
    }

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOverFolder(null);
        setDropPosition(null);
    }

    const handleDrop = (event: React.DragEvent, targetFolderId: number | null) => {
        event.preventDefault();
        event.stopPropagation();

        // Si estamos arrastrando un archivo
        if (draggedMedia) {
            handleMediaDropOnFolder(event, targetFolderId);
            return;
        }

        // Si estamos arrastrando una carpeta
        if (!draggedFolder) return;

        const draggedFolderData = allFolders.find(f => f.id === draggedFolder);
        if (!draggedFolderData) return;

        // Las carpetas solo se mueven "inside" (dentro de otra carpeta o al root)
        // No se permite reordenar carpetas

        // Determinar qué carpetas mover
        const foldersToMove = selectedItems.filter(item => item.type === 'folder').map(item => item.id);
        const isSelected = isItemSelected('folder', draggedFolder);
        const folderIds = isSelected && foldersToMove.length > 0 ? foldersToMove : [draggedFolder];

        // Filtrar carpetas que ya están en el padre destino y validar descendientes
        const folderDataToMove = allFolders.filter(f => {
            if (!folderIds.includes(f.id)) return false;
            if (f.parent_folder_id === targetFolderId) return false;
            // No permitir mover a descendientes
            if (targetFolderId !== null && isDescendant(targetFolderId, f.id)) return false;
            return true;
        });

        if (folderDataToMove.length === 0) {
            setDraggedFolder(null);
            setDragOverFolder(null);
            setDropPosition(null);
            return;
        }

        // Mover todas las carpetas en paralelo
        const promises = folderDataToMove.map(folderData => {
            const body = {
                id: folderData.id,
                parent_folder_id: targetFolderId
            };
            return moveProgramFolder(body);
        });

        Promise.all(promises)
            .then(() => {
                // Actualizar el estado local
                const updatedFolders = allFolders.map(f => {
                    const movedFolder = folderDataToMove.find(mf => mf.id === f.id);
                    return movedFolder ? { ...f, parent_folder_id: targetFolderId } : f;
                });
                setAllFolders(updatedFolders);

                // Si se movió a una carpeta, expandirla para mostrar el resultado
                if (targetFolderId !== null && !expandedFolders.has(targetFolderId)) {
                    const newExpanded = new Set(expandedFolders);
                    newExpanded.add(targetFolderId);
                    setExpandedFolders(newExpanded);
                }

                // Limpiar selección después de mover
                setSelectedItems([]);
            })
            .catch(error => {
                console.log(error);
                alert(t("Error moving folder"));
            })
            .finally(() => {
                setDraggedFolder(null);
                setDragOverFolder(null);
                setDropPosition(null);
            });
    }

    const handleDragEnd = () => {
        setDraggedFolder(null);
        setDragOverFolder(null);
        setDropPosition(null);
    }

    const isDescendant = (folderId: number, potentialAncestorId: number | null): boolean => {
        if (potentialAncestorId === null) return false;

        const folder = allFolders.find(f => f.id === folderId);
        if (!folder) return false;

        if (folder.parent_folder_id === potentialAncestorId) return true;
        if (folder.parent_folder_id === null) return false;

        return isDescendant(folder.parent_folder_id, potentialAncestorId);
    }

    // Media Drag & Drop handlers
    const handleMediaDragStart = (event: React.DragEvent, mediaId: number) => {
        event.stopPropagation();

        // Los coaches no pueden mover archivos
        if (!can_manage_content) {
            event.preventDefault();
            return;
        }

        // Si el elemento arrastrado está en la selección, arrastrar todos los seleccionados
        const isSelected = isItemSelected('media', mediaId);
        if (isSelected && selectedItems.length > 0) {
            // Arrastrar múltiples elementos
            setDraggedMedia(mediaId); // El principal
        } else {
            // Arrastrar solo este elemento y limpiar selección
            setDraggedMedia(mediaId);
            setSelectedItems([]);
        }

        event.dataTransfer.effectAllowed = 'move';
    }

    const handleMediaClick = (event: React.MouseEvent, mediaId: number) => {
        // Si presiona Ctrl/Cmd, es selección múltiple
        if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            toggleItemSelection('media', mediaId);
        }
    }

    const handleMediaDragEnd = () => {
        setDraggedMedia(null);
        setDragOverFolder(null);
        setDropPosition(null);
    }

    const handleMediaDropOnFolder = (event: React.DragEvent, targetFolderId: number | null) => {
        event.preventDefault();
        event.stopPropagation();

        if (!draggedMedia) return;

        // Determinar qué elementos mover
        const mediaToMove = selectedItems.filter(item => item.type === 'media').map(item => item.id);

        // Si el elemento arrastrado está en la selección, mover todos los seleccionados
        const isSelected = isItemSelected('media', draggedMedia);
        const mediaIds = isSelected && mediaToMove.length > 0 ? mediaToMove : [draggedMedia];

        // Filtrar archivos que ya están en la carpeta destino
        const mediaDataToMove = programMedia.filter(m =>
            mediaIds.includes(m.id) && m.folder_id !== targetFolderId
        );

        if (mediaDataToMove.length === 0) {
            setDraggedMedia(null);
            setDragOverFolder(null);
            return;
        }

        // Mover todos los archivos en paralelo
        const promises = mediaDataToMove.map(mediaData => {
            const body = {
                id: mediaData.id,
                program_id: mediaData.program_id,
                name: mediaData.name,
                description: mediaData.description,
                folder_id: targetFolderId,
                media: mediaData.media.length > 0 ? mediaData.media[0].id : 0
            };
            return updateProgramMedia(body);
        });

        Promise.all(promises)
            .then(() => {
                // Recargar los archivos de la carpeta actual
                loadMediaInFolder(currentFolderId);

                // Si se movió a una carpeta, expandirla
                if (targetFolderId !== null && !expandedFolders.has(targetFolderId)) {
                    const newExpanded = new Set(expandedFolders);
                    newExpanded.add(targetFolderId);
                    setExpandedFolders(newExpanded);
                }

                // Limpiar selección después de mover
                setSelectedItems([]);
            })
            .catch(error => {
                console.log(error);
                alert(t("Error moving file"));
            })
            .finally(() => {
                setDraggedMedia(null);
                setDragOverFolder(null);
                setDropPosition(null);
            });
    }

    // Helper function to get file icon
    const getFileIcon = (media: ProgramMedia): string => {
        if (media.media.length === 0) return 'bi-file-earmark-text';

        const mimetype = media.media[0].mimetype;
        switch (mimetype) {
            case 'application/pdf':
                return 'bi-file-earmark-pdf';
            case 'application/vnd.ms-powerpoint':
            case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            case 'application/vnd.apple.keynote':
                return 'bi-file-earmark-ppt';
            case 'application/msword':
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            case 'application/vnd.apple.pages':
                return 'bi-file-earmark-word';
            case 'application/vnd.ms-excel':
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            case 'application/vnd.apple.numbers':
                return 'bi-file-earmark-excel';
            case 'image/png':
            case 'image/jpeg':
                return 'bi-file-image';
            case 'video/mp4':
                return 'bi-file-earmark-play';
            case 'audio/*':
                return 'bi-file-earmark-music';
            case 'application/zip':
            case 'application/x-rar':
                return 'bi-file-earmark-zip';
            default:
                return 'bi-file-earmark-text';
        }
    }

    const handleBack = (event: any) => {
        event.preventDefault();
        navigate('/programs');
    }

    // File upload handlers
    const onDrop = async (acceptedFiles: File[]) => {
        // Los coaches no pueden subir archivos
        if (!can_manage_content) return;

        if (acceptedFiles.length === 0) return;

        // Límite de tamaño: 5MB para imágenes
        const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB en bytes
        const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

        // Validar tamaño de archivos antes de subir
        const filesToUpload: File[] = [];
        const rejectedFiles: {file: File, reason: string}[] = [];

        for (const file of acceptedFiles) {
            if (IMAGE_TYPES.includes(file.type) && file.size > MAX_IMAGE_SIZE) {
                rejectedFiles.push({
                    file,
                    reason: t("Image too large") + ` (${(file.size / (1024 * 1024)).toFixed(2)}MB). ${t("Maximum size for images is 5MB")}`
                });
            } else {
                filesToUpload.push(file);
            }
        }

        // Mostrar archivos rechazados
        if (rejectedFiles.length > 0) {
            const messages = rejectedFiles.map(rf => `• ${rf.file.name}: ${rf.reason}`).join('\n');
            alert(t("Some files were not uploaded") + ':\n\n' + messages);
        }

        // Si no hay archivos válidos, salir
        if (filesToUpload.length === 0) {
            return;
        }

        setUploadingFiles(true);

        // Inicializar el progreso para cada archivo
        const initialProgress = filesToUpload.map(file => ({
            name: file.name,
            progress: 0,
            status: 'uploading' as const
        }));
        setUploadProgress(initialProgress);

        // Subir archivos uno por uno
        for (let i = 0; i < filesToUpload.length; i++) {
            const file = filesToUpload[i];
            const formData = new FormData();
            formData.append('files[]', file);

            // Variable para controlar la frecuencia de actualizaciones (1 segundo)
            let lastUpdate = 0;

            try {
                // Subir el archivo con progreso
                const response = await axios.post(
                    process.env.REACT_APP_API_BASE_URL + '/wp-json/badminton/ws/v1/media',
                    formData,
                    {
                        headers: {
                            'Authorization': 'Bearer ' + localStorage.getItem('token'),
                            'Content-Type': 'multipart/form-data'
                        },
                        timeout: 600000, // 10 minutos de timeout
                        maxContentLength: Infinity,
                        maxBodyLength: Infinity,
                        onUploadProgress: (progressEvent) => {
                            const now = Date.now();
                            const percentCompleted = progressEvent.total
                                ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                                : 0;

                            // Solo actualizar cada segundo (1000ms) o al completar (100%)
                            if (now - lastUpdate >= 1000 || percentCompleted === 100) {
                                lastUpdate = now;
                                setUploadProgress(prev => prev.map((item, index) =>
                                    index === i ? { ...item, progress: percentCompleted } : item
                                ));
                            }
                        }
                    }
                );

                if (response.data && response.data.length > 0) {
                    // Crear ProgramMedia
                    const media = response.data[0];
                    const body = {
                        program_id: params.id,
                        name: media.name,
                        description: '',
                        folder_id: currentFolderId,
                        media: media.id
                    };

                    await createProgramMedia(body);

                    // Marcar como completado
                    setUploadProgress(prev => prev.map((item, index) =>
                        index === i ? { ...item, status: 'completed' as const, progress: 100 } : item
                    ));
                } else {
                    // Marcar como error
                    setUploadProgress(prev => prev.map((item, index) =>
                        index === i ? { ...item, status: 'error' as const } : item
                    ));
                }
            } catch (error: any) {
                console.error('Error uploading file:', file.name, error);

                // Mostrar mensaje de error específico
                let errorMessage = t("Error uploading file");
                if (error.code === 'ERR_NETWORK') {
                    errorMessage = t("Network error. File might be too large or connection was lost.");
                } else if (error.code === 'ECONNABORTED') {
                    errorMessage = t("Upload timeout. Try with a smaller file.");
                } else if (error.response) {
                    errorMessage = `${t("Server error")}: ${error.response.status}`;
                }

                console.error(errorMessage);

                // Marcar como error
                setUploadProgress(prev => prev.map((item, index) =>
                    index === i ? { ...item, status: 'error' as const } : item
                ));
            }
        }

        // Recargar la lista de archivos
        loadMediaInFolder(currentFolderId);

        // Esperar 2 segundos antes de ocultar el progreso
        setTimeout(() => {
            setUploadingFiles(false);
            setUploadProgress([]);
        }, 2000);
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        disabled: !can_manage_content, // Deshabilitar dropzone para coaches
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'audio/*': ['.mp3'],
            'video/mp4': ['.mp4'],
            'application/pdf': ['.pdf'],
            'application/vnd.ms-powerpoint': ['.ppt'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
            'application/vnd.apple.keynote': ['.key'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.apple.numbers': ['.numbers'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.apple.pages': ['.pages'],
            'application/zip': ['.zip'],
            'application/x-rar': ['.rar'],
            'text/plain': ['.txt']
        }
    });

    const renderFolderTree = (folders: ProgramFolder[], level: number = 0): React.ReactElement[] => {
        return folders.map(folder => {
            const isExpanded = expandedFolders.has(folder.id);
            const hasChildren = hasSubfolders(folder.id);
            const isSelected = currentFolderId === folder.id;
            const isDragging = draggedFolder === folder.id;
            const isDragOver = dragOverFolder === folder.id;
            const showDropInside = isDragOver && dropPosition === 'inside';
            const subfolders = getSubfolders(folder.id);

            return (
                <div key={folder.id} className="folder-tree-item">
                    <div
                        className={`folder-item ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${showDropInside ? 'drag-over' : ''}`}
                        style={{ paddingLeft: `${level * 20 + 12}px` }}
                        draggable={can_manage_content}
                        onDragStart={can_manage_content ? (e) => handleDragStart(e, folder.id) : undefined}
                        onDragOver={can_manage_content ? (e) => handleDragOver(e, folder.id) : undefined}
                        onDragLeave={can_manage_content ? handleDragLeave : undefined}
                        onDrop={can_manage_content ? (e) => handleDrop(e, folder.id) : undefined}
                        onDragEnd={can_manage_content ? handleDragEnd : undefined}
                        onContextMenu={can_manage_content ? (e) => handleFolderContextMenu(e, folder.id) : undefined}
                    >
                        <div className="folder-toggle" onClick={(e) => hasChildren ? handleFolderToggle(e, folder.id) : null}>
                            {hasChildren ? (
                                <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                            ) : (
                                <span className="no-toggle"></span>
                            )}
                        </div>
                        <div className="folder-content" onClick={(e) => handleFolderClick(e, folder.id)}>
                            <div className="folder-icon">
                                <i className={`bi ${isExpanded ? 'bi-folder2-open' : 'bi-folder2'}`}></i>
                            </div>
                            <div className="folder-name">{folder.name}</div>
                        </div>
                    </div>
                    {isExpanded && hasChildren && (
                        <div className="folder-children">
                            {renderFolderTree(subfolders, level + 1)}
                        </div>
                    )}
                </div>
            );
        });
    }

    return (
        <div className="program-media-folder-view-container">
            {!loaded && <LoadingSpinner />}
            {loaded &&
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h1 style={{ margin: 0 }}>
                        {t("Documents of")} <strong>{program.name}</strong>
                    </h1>
                    <Button
                        variant="outline-secondary"
                        onClick={handleBack}
                        title={t("Close")}
                    >
                        <i className="bi bi-x-lg"></i> {t("Close")}
                    </Button>
                </div>
            }
            {loaded &&
                <div className="file-management-container">
                    <div className="folders-container">
                        <div
                            className="folder-tree"
                            onContextMenu={handleAreaContextMenu}
                            onDragOver={(e) => handleDragOver(e, null)}
                            onDrop={(e) => handleDrop(e, null)}
                            onDragLeave={handleDragLeave}
                        >
                            <div className="folder-tree-item">
                                <div
                                    className={`folder-item root-folder ${currentFolderId === null ? 'selected' : ''} ${dragOverFolder === null && dropPosition === 'inside' ? 'drag-over' : ''}`}
                                    onClick={(e) => handleFolderClick(e, null as any)}
                                    onContextMenu={(e) => handleFolderContextMenu(e, null)}
                                    onDragOver={(e) => handleDragOver(e, null)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, null)}
                                >
                                    <div className="folder-toggle">
                                        <span className="no-toggle"></span>
                                    </div>
                                    <div className="folder-content">
                                        <div className="folder-icon">
                                            <i className="bi bi-house-door-fill"></i>
                                        </div>
                                        <div className="folder-name">{t("Root")} - {t("All files")}</div>
                                    </div>
                                </div>
                            </div>
                            {getRootFolders().length === 0 ? (
                                <div className="empty-folders-message">
                                    <i className="bi bi-folder2-open"></i>
                                    {can_manage_content ? (
                                        <>
                                            <p><strong>{t("No folders yet")}</strong></p>
                                            <p className="help-text">
                                                {t("Right-click here to create a new folder")}
                                            </p>
                                            <p className="help-text">
                                                {t("or use the yellow button at the bottom right")}
                                            </p>
                                        </>
                                    ) : (
                                        <p><strong>{t("No folders")}</strong></p>
                                    )}
                                </div>
                            ) : (
                                renderFolderTree(getRootFolders())
                            )}
                        </div>
                    </div>
                    <div className="files-container" {...getRootProps()}>
                        <input {...getInputProps()} />
                        {isDragActive && (
                            <div className="dropzone-overlay">
                                <div className="dropzone-content">
                                    <i className="bi bi-cloud-upload"></i>
                                    <p>{t("Drop files here to upload")}</p>
                                </div>
                            </div>
                        )}
                        {uploadingFiles && (
                            <div className="upload-overlay">
                                <div className="upload-progress-container">
                                    <h4>{t("Uploading files...")}</h4>
                                    {uploadProgress.map((file, index) => (
                                        <div key={index} className="file-upload-item">
                                            <div className="file-info">
                                                <span className="file-name">{file.name}</span>
                                                {file.status === 'completed' && <i className="bi bi-check-circle-fill text-success"></i>}
                                                {file.status === 'error' && <i className="bi bi-x-circle-fill text-danger"></i>}
                                            </div>
                                            <div className="progress">
                                                <div
                                                    className={`progress-bar ${file.status === 'completed' ? 'bg-success' : file.status === 'error' ? 'bg-danger' : ''}`}
                                                    role="progressbar"
                                                    style={{width: `${file.progress}%`}}
                                                    aria-valuenow={file.progress}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                >
                                                    {file.progress}%
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {loadingMedia && (
                            <div className="loading-overlay">
                                <LoadingSpinner />
                            </div>
                        )}
                        <div className="breadcrumb-search-row">
                            <div className="breadcrumb-container">
                                <a href="#" className="breadcrumb-item" onClick={(e) => {
                                    e.preventDefault();
                                    handleFolderClick(e, null);
                                }}>
                                    <i className="bi bi-house-door-fill"></i>
                                    <span>{t("Root")}</span>
                                </a>
                                {getBreadcrumbPath().map((folder, index) => (
                                    <span key={folder.id}>
                                        <i className="bi bi-chevron-right breadcrumb-separator"></i>
                                        <a href="#" className="breadcrumb-item" onClick={(e) => {
                                            e.preventDefault();
                                            handleFolderClick(e, folder.id);
                                        }}>
                                            {folder.name}
                                        </a>
                                    </span>
                                ))}
                            </div>
                            <div className="sort-and-search">
                                <div className="sort-controls">
                                    <Form.Select
                                        value={sortMode}
                                        onChange={(e) => setSortMode(e.target.value as 'alphabetical' | 'date')}
                                        size="sm"
                                        className="sort-selector"
                                    >
                                        <option value="alphabetical">{t("A-Z")}</option>
                                        <option value="date">{t("By date")}</option>
                                    </Form.Select>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={toggleSortDirection}
                                        className="sort-direction-btn"
                                        title={sortDirection === 'asc' ? t("Ascending") : t("Descending")}
                                    >
                                        <i className={`bi ${sortDirection === 'asc' ? 'bi-sort-alpha-down' : 'bi-sort-alpha-up'}`}></i>
                                    </Button>
                                </div>
                                <div className="search-container">
                                    <Form.Control
                                        type="text"
                                        placeholder={t("Search...")}
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                            </div>
                        </div>
                        {currentFolderId !== null && (() => {
                            const currentFolder = allFolders.find(f => f.id === currentFolderId);
                            return currentFolder && currentFolder.description ? (
                                <div className="folder-description-box">
                                    <i className="bi bi-info-circle"></i>
                                    <span>{currentFolder.description}</span>
                                </div>
                            ) : null;
                        })()}
                        <div className="media-list-container">
                            {getFilteredSubfolders().length === 0 && getFilteredMedia().length === 0 ? (
                                <div className="empty-media-message">
                                    <i className="bi bi-file-earmark-text"></i>
                                    {searchTerm ? (
                                        <p>{t("No results found")}</p>
                                    ) : can_manage_content ? (
                                        <>
                                            <p>{t("No files in this folder")}</p>
                                            <p className="help-text">
                                                {t("Drag files here to upload them to")} <strong>
                                                    {currentFolderId === null
                                                        ? t("Root")
                                                        : allFolders.find(f => f.id === currentFolderId)?.name || t("this folder")
                                                    }
                                                </strong>
                                            </p>
                                        </>
                                    ) : (
                                        <p>{t("This folder is empty")}</p>
                                    )}
                                </div>
                            ) : (
                                <div className="media-list">
                                    {sortMode === 'alphabetical' ? (
                                        <>
                                            {/* Mostrar subcarpetas primero */}
                                            {getFilteredSubfolders().map(folder => {
                                        const isFolderDragging = draggedFolder === folder.id;
                                        const isFolderDragOver = dragOverFolder === folder.id;
                                        const isFileDragOver = isFolderDragOver && draggedMedia !== null && dropPosition === 'inside';
                                        const isFolderSelected = isItemSelected('folder', folder.id);
                                        return (
                                            <div
                                                key={`folder-${folder.id}`}
                                                className={`media-item folder-item-list ${isFolderDragging ? 'dragging' : ''} ${isFileDragOver ? 'file-drag-over' : ''} ${isFolderSelected ? 'selected' : ''}`}
                                                draggable={can_manage_content}
                                                onDragStart={can_manage_content ? (e) => handleDragStart(e, folder.id) : undefined}
                                                onDragOver={can_manage_content ? (e) => handleDragOverInList(e, folder.id) : undefined}
                                                onDragLeave={can_manage_content ? handleDragLeave : undefined}
                                                onDrop={can_manage_content ? (e) => handleDrop(e, folder.id) : undefined}
                                                onDragEnd={can_manage_content ? handleDragEnd : undefined}
                                                onClick={(e) => handleFolderClick(e, folder.id)}
                                                onContextMenu={can_manage_content ? (e) => handleFolderContextMenu(e, folder.id) : undefined}
                                            >
                                                <div className="media-icon">
                                                    <i className="bi bi-folder2"></i>
                                                </div>
                                                <div className="media-info">
                                                    <div className="media-name folder-name-list">
                                                        {folder.name}
                                                    </div>
                                                    <div className="media-meta">
                                                        {moment(folder.changed_at).format("DD/MM/YYYY HH:mm")}
                                                        {folder.description && (
                                                            <>
                                                                <span className="separator">•</span>
                                                                <span className="description">{folder.description}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {/* Mostrar archivos después */}
                                    {getFilteredMedia().map(media => {
                                        const isDragging = draggedMedia === media.id;
                                        const isSelected = isItemSelected('media', media.id);
                                        return (
                                            <div
                                                key={media.id}
                                                className={`media-item ${isDragging ? 'dragging' : ''} ${isSelected ? 'selected' : ''}`}
                                                draggable={can_manage_content}
                                                onDragStart={can_manage_content ? (e) => handleMediaDragStart(e, media.id) : undefined}
                                                onDragEnd={can_manage_content ? handleMediaDragEnd : undefined}
                                                onClick={(e) => handleMediaClick(e, media.id)}
                                                onContextMenu={can_manage_content ? (e) => handleMediaContextMenu(e, media.id) : undefined}
                                            >
                                                <div className="media-icon">
                                                    <i className={`bi ${getFileIcon(media)}`}></i>
                                                </div>
                                                <div className="media-info">
                                                    <Link className="media-name" to={'/programs/media/' + media.id} target="_blank" rel="noopener noreferrer">
                                                        {media.name}
                                                    </Link>
                                                    <div className="media-meta">
                                                        {moment(media.changed_at).format("DD/MM/YYYY HH:mm")}
                                                        {media.description && (
                                                            <>
                                                                <span className="separator">•</span>
                                                                <span className="description">{media.description}</span>
                                                            </>
                                                        )}
                                                        {media.comments_count !== undefined && media.comments_count > 0 && (
                                                            <>
                                                                <span className="separator">•</span>
                                                                <span className="comments-count">
                                                                    <i className="bi bi-chat-dots"></i> {media.comments_count}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                {media.media.length > 0 && (
                                                    <div className="media-actions visible">
                                                        <a
                                                            href={media.media[0].url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="icon-link download"
                                                            title={t("Download")}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <i className="bi bi-download"></i>
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                        </>
                                    ) : (
                                        <>
                                            {/* Ordenar por fecha: mezclar carpetas y archivos */}
                                            {getAllItemsSortedByDate().map((item, index) => {
                                                if (item.type === 'folder') {
                                                    const folder = item.item as ProgramFolder;
                                                    const isFolderDragging = draggedFolder === folder.id;
                                                    const isFolderDragOver = dragOverFolder === folder.id;
                                                    const isFileDragOver = isFolderDragOver && draggedMedia !== null && dropPosition === 'inside';
                                                    const isFolderSelected = isItemSelected('folder', folder.id);
                                                    return (
                                                        <div
                                                            key={`folder-${folder.id}`}
                                                            className={`media-item folder-item-list ${isFolderDragging ? 'dragging' : ''} ${isFileDragOver ? 'file-drag-over' : ''} ${isFolderSelected ? 'selected' : ''}`}
                                                            draggable={can_manage_content}
                                                            onDragStart={can_manage_content ? (e) => handleDragStart(e, folder.id) : undefined}
                                                            onDragOver={can_manage_content ? (e) => handleDragOverInList(e, folder.id) : undefined}
                                                            onDragLeave={can_manage_content ? handleDragLeave : undefined}
                                                            onDrop={can_manage_content ? (e) => handleDrop(e, folder.id) : undefined}
                                                            onDragEnd={can_manage_content ? handleDragEnd : undefined}
                                                            onClick={(e) => handleFolderClick(e, folder.id)}
                                                            onContextMenu={can_manage_content ? (e) => handleFolderContextMenu(e, folder.id) : undefined}
                                                        >
                                                            <div className="media-icon">
                                                                <i className="bi bi-folder2"></i>
                                                            </div>
                                                            <div className="media-info">
                                                                <div className="media-name folder-name-list">
                                                                    {folder.name}
                                                                </div>
                                                                <div className="media-meta">
                                                                    {moment(folder.changed_at).format("DD/MM/YYYY HH:mm")}
                                                                    {folder.description && (
                                                                        <>
                                                                            <span className="separator">•</span>
                                                                            <span className="description">{folder.description}</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                } else {
                                                    const media = item.item as ProgramMedia;
                                                    const isDragging = draggedMedia === media.id;
                                                    const isSelected = isItemSelected('media', media.id);
                                                    return (
                                                        <div
                                                            key={`media-${media.id}`}
                                                            className={`media-item ${isDragging ? 'dragging' : ''} ${isSelected ? 'selected' : ''}`}
                                                            draggable={can_manage_content}
                                                            onDragStart={can_manage_content ? (e) => handleMediaDragStart(e, media.id) : undefined}
                                                            onDragEnd={can_manage_content ? handleMediaDragEnd : undefined}
                                                            onClick={(e) => handleMediaClick(e, media.id)}
                                                            onContextMenu={can_manage_content ? (e) => handleMediaContextMenu(e, media.id) : undefined}
                                                        >
                                                            <div className="media-icon">
                                                                <i className={`bi ${getFileIcon(media)}`}></i>
                                                            </div>
                                                            <div className="media-info">
                                                                <Link className="media-name" to={'/programs/media/' + media.id} target="_blank" rel="noopener noreferrer">
                                                                    {media.name}
                                                                </Link>
                                                                <div className="media-meta">
                                                                    {moment(media.changed_at).format("DD/MM/YYYY HH:mm")}
                                                                    {media.description && (
                                                                        <>
                                                                            <span className="separator">•</span>
                                                                            <span className="description">{media.description}</span>
                                                                        </>
                                                                    )}
                                                                    {media.comments_count !== undefined && media.comments_count > 0 && (
                                                                        <>
                                                                            <span className="separator">•</span>
                                                                            <span className="comments-count">
                                                                                <i className="bi bi-chat-dots"></i> {media.comments_count}
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {media.media.length > 0 && (
                                                                <div className="media-actions visible">
                                                                    <a
                                                                        href={media.media[0].url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="icon-link download"
                                                                        title={t("Download")}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <i className="bi bi-download"></i>
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            })}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            }
            {loaded && showAddNewMedia &&
                <ProgramMediaNew program_id={params.id} folder_id={currentFolderId} onSave={handleAddNewMediaOnSave} onClose={handleAddNewMediaOnClose} />
            }
            {loaded && showEditMedia && (selectedMediaId != 0) &&
                <ProgramMediaEdit id={selectedMediaId} onSave={handleEditMediaOnSave} onClose={handleEditMediaOnClose} />
            }
            {loaded && showDeleteMedia && (selectedMediaId != 0) &&
                <ProgramMediaDelete id={selectedMediaId} onDelete={handleDeleteMediaOnDelete} onClose={handleDeleteMediaOnClose} />
            }
            {contextMenu && can_manage_content && (
                <div
                    className="context-menu"
                    style={{
                        position: 'fixed',
                        top: contextMenu.y,
                        left: contextMenu.x,
                        zIndex: 1000
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {contextMenu.mediaId !== null ? (
                        <>
                            <div className="context-menu-item" onClick={(e) => {
                                e.preventDefault();
                                setSelectedMediaId(contextMenu.mediaId!);
                                setShowEditMedia(true);
                                setContextMenu(null);
                            }}>
                                <i className="bi bi-pencil-square"></i> {t("Edit media")}
                            </div>
                            <div className="context-menu-item delete" onClick={(e) => {
                                e.preventDefault();
                                setSelectedMediaId(contextMenu.mediaId!);
                                setShowDeleteMedia(true);
                                setContextMenu(null);
                            }}>
                                <i className="bi bi-trash"></i> {t("Delete media")}
                            </div>
                        </>
                    ) : contextMenu.folderId === -1 ? (
                        <>
                            <div className="context-menu-item" onClick={(e) => handleAddNewFolder(e, null)}>
                                <i className="bi bi-folder-plus"></i> {t("New folder")}
                            </div>
                            <div className="context-menu-item" onClick={(e) => handleAddNewMedia(e, null)}>
                                <i className="bi bi-file-earmark-plus"></i> {t("New media")}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="context-menu-item" onClick={(e) => handleAddNewFolder(e, contextMenu.folderId)}>
                                <i className="bi bi-folder-plus"></i> {t("New subfolder")}
                            </div>
                            <div className="context-menu-item" onClick={(e) => handleAddNewMedia(e, contextMenu.folderId)}>
                                <i className="bi bi-file-earmark-plus"></i> {t("New media")}
                            </div>
                            <div className="context-menu-divider"></div>
                            <div className="context-menu-item" onClick={(e) => handleEditFolder(e, contextMenu.folderId!)}>
                                <i className="bi bi-pencil-square"></i> {t("Edit folder")}
                            </div>
                            <div className="context-menu-item delete" onClick={(e) => handleDeleteFolder(e, contextMenu.folderId!)}>
                                <i className="bi bi-trash"></i> {t("Delete folder")}
                            </div>
                        </>
                    )}
                </div>
            )}
            {loaded && showAddNewFolder &&
                <ProgramFolderNew program_id={params.id} parent_folder_id={parentFolderIdForNew} onSave={handleAddNewFolderOnSave} onClose={handleAddNewFolderOnClose} />
            }
            {loaded && showEditFolder && (selectedFolderId != 0) &&
                <ProgramFolderEdit id={selectedFolderId} onSave={handleEditFolderOnSave} onClose={handleEditFolderOnClose} />
            }
            {loaded && showDeleteFolder && (selectedFolderId != 0) &&
                <ProgramFolderDelete
                    id={selectedFolderId}
                    folderName={allFolders.find(f => f.id === selectedFolderId)?.name || ''}
                    subfolders={getAllDescendantFolders(selectedFolderId).map(f => ({ id: f.id, name: f.name }))}
                    mediaFiles={allProgramMedia.filter(m => {
                        const folderIds = [selectedFolderId, ...getAllFolderIds(selectedFolderId)];
                        return m.folder_id !== null && folderIds.includes(m.folder_id);
                    }).map(m => ({ id: m.id, name: m.name }))}
                    onDelete={handleDeleteFolderOnDelete}
                    onClose={handleDeleteFolderOnClose}
                />
            }
        </div>
    )
}
